import PayPalLogo from '@betterplace/assets/images/logos/paypal.svg'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErrorBoundary, JSONFetcher, LogoSpinner, handleError, scrollToNode } from 'shared'
import { FormValues } from 'donate/types'
import {
  PayPalButtons,
  PayPalButtonsComponentProps,
  PayPalScriptProvider,
  ReactPayPalScriptOptions,
  SCRIPT_LOADING_STATE,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js'
import { redirect } from './submit_handler'
import { useFormikContext } from 'formik'
import { useNetworkStatus } from 'shared'

function usePaypalCreateOrderOrBillingAgreementCallback(): () => Promise<string> {
  const { values, submitForm, setSubmitting } = useFormikContext<FormValues>()
  const formValueRef: React.MutableRefObject<FormValues | null> = useRef(null)

  useEffect(() => {
    formValueRef.current = values
  }, [values])

  const onPaypalClicked = useCallback(async () => {
    await submitForm().catch((error) => handleError(error, i18n.t('errors.messages.submit')))
    setSubmitting(true) // keep it looking busy
    return (formValueRef.current && formValueRef.current._paypal_token) ?? ''
  }, [setSubmitting, submitForm])

  return onPaypalClicked
}

const useFieldErrorsKeys = () => {
  const { errors } = useFormikContext<FormValues>()
  const keys = useMemo(() => Object.keys(errors ?? {}).filter((key) => key !== 'payment_method'), [errors])
  return keys
}

/**
 * Function skips the "general"  payment_method error, so that we only evaluate the validity of the field itself
 * @returns boolean
 */
const useIsFormValid = () => {
  const { isValid } = useFormikContext<FormValues>()
  const keys = useFieldErrorsKeys()
  return isValid || !keys.length
}

const usePaypalOptions = (clientId?: string) => {
  const [options, setOptions] = useState<ReactPayPalScriptOptions>({
    'client-id': '',
    'commit': true,
    'currency': 'EUR',
    'intent': 'tokenize',
    'disable-funding': 'card,sepa',
    'vault': true,
    'locale': i18n.locale === 'de' ? 'de_DE' : 'en_US',
    'data-react-paypal-script-id': 'sdk-paypal-script',
    'data-namespace': 'sdk-paypal', // solves the issue with paypal namespace conflicting with input#paypal
    'components': 'buttons',
  })
  useEffect(() => {
    setOptions((old) => ({ ...old, 'client-id': clientId ?? '' }))
  }, [clientId])
  return options['client-id'] ? options : undefined
}

const usePayPalButtonProps = (): PayPalButtonsComponentProps & { error?: PayPalFatalError } => {
  const { setSubmitting, values, isSubmitting, setFieldValue } = useFormikContext<FormValues>()
  const valuesRef = useRef<FormValues | null>(null)
  const isValid = useIsFormValid()

  useEffect(() => {
    valuesRef.current = values
  }, [values])

  const createBillingAgreement = usePaypalCreateOrderOrBillingAgreementCallback()

  const onApprove = useCallback(async () => {
    if (valuesRef.current && valuesRef.current._redirect_to) {
      await redirect({ to: valuesRef.current._redirect_to, setFieldValue, values: valuesRef.current })
    }
  }, [setFieldValue])

  const onCancel = useCallback(() => {
    setSubmitting(false)
  }, [setSubmitting])

  const [error, setError] = useState<PayPalFatalError | undefined>(undefined)

  const onError = useCallback(
    (e) => {
      setSubmitting(false)

      if (/(?:\b|_)429/.test(e)) {
        setError(PayPalFatalError.TooManyRequests)

        const [corrId] = /corr id: \w+/i.exec(e) || ['']

        let os
        if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) os = 'ios'
        else if (/Macintosh/i.test(navigator.userAgent)) os = 'mac'
        else if (/Android/i.test(navigator.userAgent)) os = 'droid'
        else if (/Windows/i.test(navigator.userAgent)) os = 'win'
        else os = 'n/a'

        JSONFetcher.post({
          url: '/browser_events',
          body: { event_name: ['paypal_429', corrId, os].filter(Boolean).join(':') },
        })
        return
      }
      /**
       * TODO: Better error handling: according to docs these errors should not be handled in any particular way and thus are considered final
       *  https://developer.paypal.com/docs/checkout/standard/customize/handle-errors/#link-buyercheckouterror
       *  We can potentially reload the whole integration, but we should not just enable the user to mash the button as they please
       setError(PayPalFatalError.Other)
       */
    },
    [setSubmitting, setError]
  )

  const env = useMemo(() => (values._debug ? 'sandbox' : 'production'), [values._debug])

  const props = useMemo(
    () => ({
      onCancel,
      onError,
      isSubmitting,
      createBillingAgreement,
      onApprove,
      error,
      env,
      style: buttonStyle,
      disabled: !isValid,
    }),
    [onCancel, onError, isSubmitting, createBillingAgreement, onApprove, error, env, isValid]
  )
  return props
}

const PaypalButton_ = ({ clientId, onClick }: { clientId?: string; onClick?: () => void }) => {
  const options = usePaypalOptions(clientId)
  if (!options) return <NoPaypalJsMessage />
  return (
    <ErrorBoundary>
      <PayPalScriptProvider deferLoading options={options}>
        <PayPalButtonInner onClick={onClick} />
      </PayPalScriptProvider>
    </ErrorBoundary>
  )
}
export const PaypalButton = React.memo(PaypalButton_)

enum PayPalButtonStatus {
  Initial,
  ReadyToLoad,
  FieldError,
  FatalError,
  Ok,
  Busy,
  Offline,
}

enum PayPalFatalError {
  LoadingFailed,
  TooManyRequests,
  UnsupportedBrowser,
  Other,
}

const usePayPalStatus = (props: Pick<ReturnType<typeof usePayPalButtonProps>, 'error'> | undefined) => {
  const [{ isResolved, isRejected, isInitial, isPending }] = usePayPalScriptReducer()
  const { dirty, isSubmitting } = useFormikContext<FormValues>()
  const networkStatus = useNetworkStatus()
  const isValid = useIsFormValid()
  const isOffline = networkStatus === 'offline'
  const isUnsupportedBrowser = /Twitter/i.test(navigator.userAgent)
  const result = useMemo(() => {
    if (!props) return [PayPalButtonStatus.Initial, undefined] as const
    if (props.error) return [PayPalButtonStatus.FatalError, props.error] as const
    if (isUnsupportedBrowser) return [PayPalButtonStatus.FatalError, PayPalFatalError.UnsupportedBrowser] as const
    if (isInitial && isValid && dirty) return [PayPalButtonStatus.ReadyToLoad, undefined] as const
    if (isInitial) return [PayPalButtonStatus.Initial, undefined] as const
    if (isPending || isSubmitting) return [PayPalButtonStatus.Busy, undefined] as const
    if (!isValid) return [PayPalButtonStatus.FieldError, undefined] as const
    if (isOffline) return [PayPalButtonStatus.Offline, undefined] as const
    if (isResolved) return [PayPalButtonStatus.Ok, undefined] as const
    if (isRejected) return [PayPalButtonStatus.FatalError, PayPalFatalError.LoadingFailed] as const
    return [PayPalButtonStatus.Ok, undefined] as const
  }, [
    dirty,
    isInitial,
    isOffline,
    isPending,
    isRejected,
    isResolved,
    isSubmitting,
    isUnsupportedBrowser,
    isValid,
    props,
  ])
  return result
}

const useLoadPayPalWhenReady = (status: PayPalButtonStatus) => {
  const [_, dispatch] = usePayPalScriptReducer()
  useEffect(() => {
    if (status !== PayPalButtonStatus.ReadyToLoad) return
    dispatch({ type: 'setLoadingStatus', value: SCRIPT_LOADING_STATE.PENDING })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])
}

const PayPalButtonInner = ({ onClick }: { onClick?: () => void }) => {
  const { error, ...props } = usePayPalButtonProps()
  const [status, fatalError] = usePayPalStatus({ error })
  useLoadPayPalWhenReady(status)
  if (status === PayPalButtonStatus.FatalError) return <FatalErrorMessage error={fatalError} />
  const busy = status === PayPalButtonStatus.Busy
  const ok = status === PayPalButtonStatus.Ok
  return (
    <div style={containerStyle}>
      <DummyPayPalButton busy={busy} />
      <div style={!ok ? { display: 'none' } : {}}>
        <PayPalButtons onClick={onClick} {...props} />
      </div>
    </div>
  )
}

const DummyPayPalButton = ({ busy }: { busy?: boolean }) => {
  const errorFields = useFieldErrorsKeys()
  const onClick = useCallback(() => {
    const firstError = errorFields[0]
    if (!firstError || busy) return
    const field: HTMLElement | null = document.querySelector(`[name="${firstError}"]`)
    if (!field) return
    scrollToNode(field)
    field.focus()
  }, [errorFields, busy])

  return (
    <div style={dummyButtonStyle}>
      <button
        style={{ height: buttonHeight }}
        className="flex items-center justify-center w-full"
        onClick={onClick}
        disabled={busy}
      >
        <div>
          {busy && <LogoSpinner style={{ display: 'inline-block' }} />}
          <img style={{ display: 'inline-block' }} src={PayPalLogo} alt={i18n.t('verbs.submit')} />
        </div>
      </button>
    </div>
  )
}

const FatalErrorMessage = ({ error }: { error?: PayPalFatalError }) => {
  if (typeof error === 'undefined') return null
  switch (error) {
    case PayPalFatalError.TooManyRequests:
      return <Error429Message />
    case PayPalFatalError.LoadingFailed:
      return <NoPaypalJsMessage />
    case PayPalFatalError.UnsupportedBrowser:
      return <UnsupportedBrowserMessage />
    default:
      return <OtherErrorMessage />
  }
}

const NoPaypalJsMessage = () => <p className="generic-value negative">{i18n.t('donate.errors.paypal_ad_blocker')}</p>

const Error429Message = () => (
  <>
    <p className="generic-value negative">{i18n.t('donate.errors.paypal_too_many_requests')}</p>
    <p className="generic-value negative">{i18n.t('donate.errors.paypal_too_many_requests_settings')}</p>
  </>
)

const OtherErrorMessage = () => <p className="generic-value negative">{i18n.t('donate.errors.paypal_other')}</p>

const UnsupportedBrowserMessage = () => (
  <p className="generic-value negative">{i18n.t('donate.errors.paypal_unsupported_browser')}</p>
)

const buttonHeight = 50

const containerStyle: React.CSSProperties = {
  minWidth: 200,
  maxWidth: 300,
  background: '#f6c439',
  borderRadius: 4,
  boxShadow: '2px 2px 3px grey',
  lineHeight: '10px',
  height: buttonHeight,
  zIndex: 1,
  position: 'relative',
  marginBlockStart: '1.25rem',
}

const buttonStyle: PayPalButtonsComponentProps['style'] = {
  height: buttonHeight,
  color: 'gold',
  fundingicons: false, // does not exist on props thus casting bellow
  label: 'checkout',
  shape: 'rect',
  size: 'responsive',
  tagline: false,
} as PayPalButtonsComponentProps['style']

const dummyButtonStyle: React.CSSProperties = {
  height: buttonHeight,
  margin: 0,
  textAlign: 'center',
  zIndex: -1,
  position: 'absolute',
  left: 0,
  right: 0,
}
